import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, createTheme, fetchThemes, prepareAppCheck } from '../api/client';
import type { ThemeSummary } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { AdSlot } from '../components/AdSlot';
import { TopShowcase } from '../components/TopShowcase';
import { Portrait } from '../components/Portrait';
import { perDayMax, useSite } from '../site/SiteProvider';
import { DEFAULT_TYPE_COUNT, PLANNED_THEMES, PROMPT_EXAMPLES, PROMPT_MAX, TYPE_COUNTS, roundTypeCount } from '../top/catalog';

type MakerState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'generating'; readonly startedAt: number }
  | { readonly phase: 'failed'; readonly message: string };

/**
 * GenDan のトップ（`/`）。
 *
 * このサイトは「プロンプトを書くとタイプ診断がひとつ生まれ、SNS でシェアして遊べる」場所（README §1）。
 * 1. 人気の診断 — 運営が用意した診断も、みんなが作った診断も同じ土俵で、受けた回数順（`GET /v1/themes`）
 * 2. 自分で作る — テーマとキャラ数（既定 16）を送ると Gemini がキャラを考え、その診断の入口 `/t/:themeId` へ移る。
 *    キャラ数は 4・8・16・32 から。自由に打った数は近いものに丸める（診断の軸は 2 のべき乗にしか分けられない）
 */
export function HomePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { limits } = useSite();
  /** null は読み込み中。取れなかったときは空（「自分で作る」は使える）。 */
  const [themes, setThemes] = useState<readonly ThemeSummary[] | null>(null);
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(DEFAULT_TYPE_COUNT);
  const [countText, setCountText] = useState(String(DEFAULT_TYPE_COUNT));
  const [countNote, setCountNote] = useState('');
  const [maker, setMaker] = useState<MakerState>({ phase: 'idle' });
  const [elapsed, setElapsed] = useState(0);
  const makerRef = useRef<HTMLElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchThemes()
      .then((response) => { if (!cancelled) setThemes(response.themes); })
      .catch(() => { if (!cancelled) setThemes([]); }); // 取れなくても「自分で作る」は使える
    return () => { cancelled = true; };
  }, []);

  // 生成は 30〜100 秒かかる。待たされている感じを減らすため経過秒を出す。
  useEffect(() => {
    if (maker.phase !== 'generating') return;
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - maker.startedAt) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [maker]);

  const fillPrompt = useCallback((text: string) => {
    setPrompt(text.slice(0, PROMPT_MAX));
    setMaker({ phase: 'idle' });
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    makerRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
  }, []);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const text = prompt.trim();
      if (text.length < 2) {
        setMaker({ phase: 'failed', message: 'どんな診断にしたいか、テーマを書いてください。' });
        return;
      }
      setElapsed(0);
      setMaker({ phase: 'generating', startedAt: Date.now() });
      try {
        const created = await createTheme(text, count);
        // 絵を 1 回ぶん使ったので、ヘッダーの「今日あと n 回」を取り直す
        void auth.refresh();
        navigate(`/t/${created.themeId}`, {
          state: {
            justCreated: true, fallback: created.fallback, typeCount: created.typeCount, requestedCount: created.requestedCount,
            imageStatus: created.images?.status ?? null,
          },
        });
      } catch (cause) {
        setMaker({ phase: 'failed', message: cause instanceof ApiError ? cause.message : '診断を作れませんでした。もう一度お試しください。' });
      }
    },
    [auth, count, navigate, prompt],
  );

  const generating = maker.phase === 'generating';

  // キャラ数。−／＋は選べる数を一段ずつ、入力欄は好きな数を受けて近いものに丸める
  const applyCount = useCallback((raw: number) => {
    const rounded = roundTypeCount(raw);
    setCount(rounded);
    setCountText(String(rounded));
    setCountNote(Number.isFinite(raw) && raw !== rounded ? `${Math.round(raw)} には分けられないので ${rounded} にしました。` : '');
  }, []);
  const stepCount = useCallback((direction: -1 | 1) => {
    const index = TYPE_COUNTS.indexOf(count);
    const next = TYPE_COUNTS[Math.min(TYPE_COUNTS.length - 1, Math.max(0, index + direction))];
    applyCount(next);
  }, [applyCount, count]);

  return (
    <main className="top">
      <section className="top__hero">
        <h1 className="top__title">
          テーマを書くだけで、
          <br />
          タイプ診断がひとつ生まれる。
        </h1>
        <p className="top__lead">
          「日本むかし話」と書けば、桃太郎や浦島太郎、お供の犬や猿があなたの性格を映すキャラクターになります。できた診断はリンクひとつで友達に送れて、結果もそのまま SNS に流せます。
        </p>
        <div className="top__actions">
          <button className="button button--primary" type="button" onClick={() => fillPrompt(prompt)}>
            自分で診断を作る
          </button>
        </div>
      </section>

      <section className="top__section" aria-labelledby="popular-heading">
        <div className="top__section-head">
          <h2 className="top__heading" id="popular-heading">人気の診断</h2>
          <p className="top__section-note">受けた人が多い順。押すとその診断の入口に移ります。友達が作った診断も、ここに並びます。</p>
        </div>
        {themes === null && <p className="top__section-note" role="status">読み込んでいます…</p>}
        {themes?.length === 0 && <p className="top__section-note">いまは並べられる診断がありません。下の「自分で作る」から作れます。</p>}
        <ol className="catalog">
          {(themes ?? []).map((theme, index) => (
            <li key={theme.themeId} className="catalog-card">
              <span className="catalog-card__rank" aria-label={`${index + 1}位`}>{index + 1}</span>
              <ThemeCardBody theme={theme} />
            </li>
          ))}
        </ol>
      </section>

      <AdSlot placement="home" />

      <section className="top__section maker" ref={makerRef} aria-labelledby="maker-heading">
        <div className="top__section-head">
          <h2 className="top__heading" id="maker-heading">自分で作る</h2>
          <p className="top__section-note">
            テーマを一文で書いてください。診断の入口とキャラクターを Gemini が考えます。設問は「ふだんどうしているか」を訊く形にそろえ、結果は断定せず「傾向」として伝えます。名前やメールアドレスは書かないでください。
          </p>
          <ImageAccessNote />
        </div>

        {/* 書きはじめたら reCAPTCHA（App Check）を先に読む。トップを見るだけの人には読ませない */}
        <form className="maker__form" onSubmit={onSubmit} onFocus={prepareAppCheck}>
          <label className="maker__label" htmlFor="theme-prompt">どんな診断にしますか</label>
          <textarea
            id="theme-prompt"
            ref={textareaRef}
            className="maker__textarea"
            value={prompt}
            maxLength={PROMPT_MAX}
            rows={3}
            placeholder="例：日本むかし話診断"
            onChange={(event) => {
              setPrompt(event.target.value);
              if (maker.phase === 'failed') setMaker({ phase: 'idle' });
            }}
            disabled={generating}
          />
          <div className="maker__row">
            <span className="maker__count" aria-live="polite">{prompt.length} / {PROMPT_MAX}</span>
          </div>

          <div className="maker__row maker__row--count">
            <div className="count-picker" role="group" aria-labelledby="count-label">
              <span className="maker__label count-picker__label" id="count-label">キャラクターの数</span>
              <button className="count-picker__step" type="button" onClick={() => stepCount(-1)} disabled={generating || count === TYPE_COUNTS[0]} aria-label="減らす">−</button>
              <input
                className="count-picker__input"
                type="number"
                inputMode="numeric"
                min={TYPE_COUNTS[0]}
                max={TYPE_COUNTS[TYPE_COUNTS.length - 1]}
                value={countText}
                aria-labelledby="count-label"
                disabled={generating}
                onChange={(event) => setCountText(event.target.value)}
                onBlur={() => applyCount(Number(countText))}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); applyCount(Number(countText)); } }}
              />
              <button className="count-picker__step" type="button" onClick={() => stepCount(1)} disabled={generating || count === TYPE_COUNTS[TYPE_COUNTS.length - 1]} aria-label="増やす">＋</button>
              <span className="count-picker__hint">{TYPE_COUNTS.join('・')} から。それ以外の数は近いものになります</span>
            </div>
            <button className="button button--primary" type="submit" disabled={generating}>
              {generating ? '診断を作っています…' : 'この設定で診断を作る'}
            </button>
          </div>

          {generating && (
            <div className="maker__progress" role="status" aria-live="polite">
              <span className="maker__spinner" aria-hidden="true" />
              <div>
                <p className="maker__progress-title">キャラクター {count} 人を考えています（{elapsed} 秒）</p>
                <p className="maker__progress-body">30 秒〜1 分ほどかかります。できあがると診断の入口に移ります。このページを閉じないでください。</p>
              </div>
            </div>
          )}
          {countNote && !generating && <p className="count-picker__note" role="status">{countNote}</p>}
          {maker.phase === 'failed' && <p className="maker__error" role="alert">{maker.message}</p>}

          <div className="maker__examples" aria-label="書き方の例">
            <span className="maker__examples-label">例から入れる</span>
            {PROMPT_EXAMPLES.map((example) => (
              <button key={example} className="chip" type="button" onClick={() => fillPrompt(example)} disabled={generating}>
                {example}
              </button>
            ))}
          </div>
        </form>

        <ul className="seeds" aria-label="こんな診断が作れます">
          {PLANNED_THEMES.map((seed) => (
            <li key={seed.id} className="seed">
              <span className="seed__emoji" aria-hidden="true">{seed.emoji}</span>
              <div className="seed__text">
                <p className="seed__title">{seed.title}</p>
                <p className="seed__catch">{seed.catch}</p>
              </div>
              <button className="button button--ghost seed__action" type="button" onClick={() => fillPrompt(seed.prompt)} disabled={generating}>
                この案で作る
              </button>
            </li>
          ))}
        </ul>
      </section>

      <TopShowcase />

      <p className="top__footnote">
        診断はログインなしで最後まで受けられます。結果ページの URL を知る人だけが結果を見られ、氏名やメールは載りません。ログインすると、作った診断にキャラクターの絵が付き（{perDayMax(limits.userImageDailyLimit)}）、自分の診断を管理できます。
      </p>
    </main>
  );
}

/**
 * 絵が付く条件の案内（doc/AUTH.md §5）。
 * 絵は Gemini の画像モデルを 1 人 1 枚ぶん呼ぶので、費用がかかる。だからログインした人だけ、1 日の回数つき（管理メニューの「上限」で変わる）。
 * 診断そのものはログインなしで作れるし、最後まで受けられる。
 */
function ImageAccessNote() {
  const { ready, user, enabled, busy, quota, requestSignIn } = useAuth();
  const { limits } = useSite();
  if (!ready || !enabled) return null;

  if (!user) {
    return (
      <p className="maker__note">
        キャラクターの絵が付くのは、ログインして作った診断だけです（{perDayMax(limits.userImageDailyLimit)}）。ログインしなくても診断は作れて、そのまま遊べます。
        <button className="button button--ghost button--small maker__note-button" type="button" onClick={() => void requestSignIn()} disabled={busy}>
          {busy ? 'ログイン中…' : 'ログインする'}
        </button>
      </p>
    );
  }
  if (!quota || quota.limit === null || quota.remaining === null) return null;
  if (quota.limit === 0) {
    return <p className="maker__note maker__note--warn">いまはキャラクターの絵を描くのを止めています。診断はこのまま作れます。</p>;
  }
  if (quota.remaining > 0) {
    return <p className="maker__note">キャラクターの絵は <strong>今日あと {quota.remaining} 回</strong> 描けます（1 日 {quota.limit} 回まで）。</p>;
  }
  return (
    <p className="maker__note maker__note--warn">
      今日は絵の上限（{quota.limit} 回）に達しました。診断はこのまま作れます。絵は日付が変わってから「自分の診断」で描けます。
    </p>
  );
}

function ThemeCardBody({ theme }: { readonly theme: ThemeSummary }) {
  return (
    <Link className="catalog-card__body" to={theme.href}>
      <div className="catalog-card__faces" aria-hidden="true">
        {theme.sample.slice(0, 3).map((c, i) => (
          <Portrait key={`${c.name}-${i}`} brief={{ name: c.name, portrait: c.portrait, emoji: c.emoji }} size={56} className="catalog-card__face" loading="lazy" />
        ))}
      </div>
      <div className="catalog-card__text">
        <h3 className="catalog-card__title">{theme.title}</h3>
        {/* 区切りの「／」は後ろの項目に付けて、行末に「／」だけが残らないようにする */}
        <p className="catalog-card__meta">
          {theme.characterCount} タイプ {`／\u00a0${theme.subject}`}
          {theme.plays !== null && theme.plays > 0 ? ` ／\u00a0${theme.plays}\u00a0人が受けた` : ''}
        </p>
        <span className="catalog-card__go">この診断を受ける</span>
      </div>
    </Link>
  );
}
